import { html, LitElement, nothing } from "lit";
import { styleMap } from "lit/directives/style-map.js";
import { StripeWrapper } from "../../..//services/stripe-wrapper";
import "../../../legl-ui/button";
import { toastService } from "../../../legl-ui/toast";
import styles from "./stripe-component-styles.js";

export class LeglStripeComponent extends LitElement {
  static get properties() {
    return {
      amount: {},
      clientSecret: {},
      callback: {},
      acceptHigherFeeCards: {},
      initializeManually: {
        attribute: "initialize-manually",
        type: Boolean,
      },
      browserPaymentMethods: { state: true },
    };
  }

  constructor() {
    super();
    this.clientSecret = null;
  }

  // renders component to DOM - we can't use shadow DOM because Stripe needs to find <div id="card-element"></div>
  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    if (!this.initializeManually) {
      this.initializeStripe();
    }
  }

  initializeStripe() {
    this.stripeWrapper = new StripeWrapper(
      undefined,
      this.acceptHigherFeeCards,
    );
    this.stripeWrapper
      .initElement()
      // only needed for Google/Apple pay - regular card payments get amount from BE
      .initPaymentRequestButton(this.amount ? this.amount * 100 : 0);
    // check if available browserPaymentMethods - e.g Google/Apply pay
    this.stripeWrapper.paymentRequest
      .canMakePayment()
      .then((browserPaymentMethods) => {
        if (browserPaymentMethods) {
          this.browserPaymentMethods = browserPaymentMethods;
          this.stripeWrapper.paymentRequestButton.mount(
            "#payment-request-button",
          );
          this.onPaymentRequest(this.stripeWrapper.paymentRequest);
        } else {
          this.querySelector("#payment-request-button").style.display = "none";
        }
      });

    this.stripeWrapper.card.addEventListener("change", (event) => {
      this.querySelector("#payment-submit").disabled = !event.complete;

      const displayError = this.querySelector("#card-errors");
      if (event.error && event.error.message) {
        displayError.textContent = event.error.message;
      } else {
        displayError.textContent = "";
      }
    });
  }

  onPaymentRequest(paymentRequest) {
    paymentRequest.on("paymentmethod", (result) => {
      this.collectPaymentRequest = result;
      if (typeof this.callback === "function") {
        this.callback({
          payment_method_id: result.paymentMethod.id,
        });
      }
    });
  }

  disablePaymentForm() {
    this.loadingElement = document.createElement(
      "legl-fullscreen-loading-spinner",
    );
    document.body.appendChild(this.loadingElement);
    this.querySelector("#payment-submit").disabled = true;
  }

  enablePaymentForm() {
    if (this.loadingElement) {
      this.loadingElement.parentElement.removeChild(this.loadingElement);
    }
    this.querySelector("#payment-submit").disabled = false;
  }

  handleAction(clientSecret) {
    this.stripeWrapper.stripe.handleCardAction(clientSecret).then((result) => {
      if (result.error) {
        toastService.showError("Your card was declined.");
        if (typeof this.callback === "function") {
          this.callback({
            stripeError: result.error,
            cardActionError: true,
          });
        }
      } else {
        if (!this.loadingElement) {
          this.disablePaymentForm();
        }
        if (typeof this.callback === "function") {
          this.callback({});
        }
      }
    });
  }

  async handleBtnClick() {
    this.disablePaymentForm();
    try {
      const results = await this.stripeWrapper.createPaymentMethod({
        name: this.applicant_name,
      });
      if (typeof this.callback === "function") {
        this.callback({
          payment_method_id: results.paymentMethod.id,
        });
      }
    } catch (error) {
      this.enablePaymentForm();
    }
  }

  get higherFeesEnabled() {
    return this.acceptHigherFeeCards;
  }

  render() {
    return html`
            <div data-testid="pay-stripe-component">
                <div style=${styleMap(styles.stripeComponentFormRow)}>
                    <div class="stripe-component__payment-request-wrapper">
                        <div
                            id="payment-request-button"
                            style=${styleMap(styles.paymentRequestButton)}
                        ></div>
                        ${
                          !!this.browserPaymentMethods
                            ? html`
                                  <div
                                      style=${styleMap(
                                        styles.stripeComponentPaymentRequest,
                                      )}
                                  >
                                      <div style=${styleMap(styles.line)}></div>
                                      <p
                                          style=${styleMap(
                                            styles.stripeComponentPaymentRequestParagraph,
                                          )}
                                      >
                                          Or enter your card details
                                      </p>
                                      <div style=${styleMap(styles.line)}></div>
                                  </div>
                              `
                            : nothing
                        }
                    </div>
                    <div>
                        <div
                            id="card-element"
                            style=${styleMap(styles.cardElement)}
                        ></div>
                        <div
                            id="card-errors"
                            style=${styleMap(styles.cardErrors)}
                            role="alert"
                        ></div>
                    </div>
                    <div style=${styleMap(styles.stripeComponentCardIcons)}>
                        ${
                          this.browserPaymentMethods?.applePay
                            ? html`<legl-icon
                                  size="x-large"
                                  name="legl-icon-apple-pay"
                              ></legl-icon>`
                            : nothing
                        }
                        ${
                          this.browserPaymentMethods?.googlePay
                            ? html`<legl-icon
                                  size="x-large"
                                  name="legl-icon-google-pay"
                              ></legl-icon>`
                            : nothing
                        }
                        <legl-icon
                            size="x-large"
                            name="legl-icon-visa"
                        ></legl-icon>
                        <legl-icon
                            size="x-large"
                            name="legl-icon-mastercard"
                        ></legl-icon>
                        ${
                          this.higherFeesEnabled
                            ? html`<legl-icon
                                  size="x-large"
                                  name="legl-icon-amex"
                              ></legl-icon>`
                            : nothing
                        }
                    </div>
                </div>
                <legl-button
                    disabled
                    id="payment-submit"
                    full-width
                    data-cy-confirm-payment-button
                    @click="${this.handleBtnClick}"
                    >Confirm Card Payment</legl-button
                >
            </div>
        `;
  }
}

if (!customElements.get("legl-stripe-component")) {
  customElements.define("legl-stripe-component", LeglStripeComponent);
}
